import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

export interface DashboardCountedMultiSelectOption<T extends string> {
  value: T;
  label: string;
  code?: string;
  count?: number;
}

interface DashboardCountedMultiSelectProps<T extends string> {
  ariaLabel: string;
  allLabel: string;
  selectedLabel: string;
  options: Array<DashboardCountedMultiSelectOption<T>>;
  selected: T[];
  onChange: (selected: T[]) => void;
}

export function DashboardCountedMultiSelect<T extends string>({
  ariaLabel,
  allLabel,
  selectedLabel,
  options,
  selected,
  onChange,
}: DashboardCountedMultiSelectProps<T>): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionsId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [draftSelected, setDraftSelected] = useState<T[]>(selected);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const draftSelectedSet = useMemo(() => new Set(draftSelected), [draftSelected]);

  const buttonText =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (options.find((option) => selectedSet.has(option.value))?.label ?? selected[0])
        : `${selected.length} ${selectedLabel}`;

  const openMenu = () => {
    setDraftSelected(selected);
    setIsOpen(true);
  };

  const closeMenu = useCallback(() => {
    setDraftSelected(selected);
    setIsOpen(false);
  }, [selected]);

  const toggleOption = (value: T) => {
    const next = new Set(draftSelectedSet);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    setDraftSelected(
      options.filter((option) => next.has(option.value)).map((option) => option.value)
    );
  };

  const applySelection = () => {
    onChange(draftSelected);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && !containerRef.current?.contains(target)) {
        closeMenu();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMenu, isOpen]);

  return (
    <div className="dg-counted-multiselect" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="dg-counted-multiselect-button"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-controls={optionsId}
        aria-haspopup="true"
        onClick={() => (isOpen ? closeMenu() : openMenu())}
      >
        <span>{buttonText}</span>
        <span className="dg-counted-multiselect-chevron" aria-hidden="true">
          <ExpandMoreOutlinedIcon style={{ fontSize: 14 }} />
        </span>
      </button>
      {isOpen ? (
        <div
          id={optionsId}
          className="dg-counted-multiselect-menu"
          role="group"
          aria-label={`${ariaLabel} options`}
        >
          <div className="dg-counted-multiselect-options">
            {options.map((option) => (
              <label key={option.value} className="dg-counted-multiselect-option">
                <input
                  type="checkbox"
                  checked={draftSelectedSet.has(option.value)}
                  onChange={() => toggleOption(option.value)}
                />
                {option.code ? (
                  <span className="dg-counted-multiselect-code">{option.code}</span>
                ) : null}
                <span className="dg-counted-multiselect-label">{option.label}</span>
                <span className="dg-counted-multiselect-count">
                  {(option.count ?? 0).toLocaleString('en-US')}
                </span>
              </label>
            ))}
          </div>
          <div className="dg-counted-multiselect-actions">
            <button type="button" onClick={() => setDraftSelected([])}>
              All types
            </button>
            <button type="button" className="is-primary" onClick={applySelection}>
              Apply
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
